import { useMemo, useState } from "react";

import { parseDateanormRecord } from "../lib/datanormParser";
import type { ParsedField } from "../lib/datanormParser";
import { useEscapeKey } from "../lib/useEscapeKey";

export type RawRecordModalData = {
  title: string;
  sourceKind: string | null;
  supplier: string | null;
  sourceFile: string | null;
  sourceRecordRow: string | number | null;
  rawRecord: string | null;
};

export function RawRecordModal({
  record,
  onClose,
}: {
  record: RawRecordModalData | null;
  onClose: () => void;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const parsed = useMemo<ParsedField[] | null>(
    () => (record?.rawRecord ? parseDateanormRecord(record.rawRecord) : null),
    [record?.rawRecord],
  );
  useEscapeKey(onClose, record !== null);

  if (!record) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="raw-record-title"
      onMouseDown={onClose}
    >
      <div
        className="max-h-full w-full max-w-3xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2
              id="raw-record-title"
              className="text-sm font-semibold text-slate-900"
            >
              {record.title}
            </h2>
            <p className="mt-1 truncate text-xs text-slate-500">
              {record.supplier ? `${record.supplier} · ` : ""}
              {record.sourceFile ?? "Quelle unbekannt"}
              {record.sourceRecordRow != null
                ? ` · Zeile ${record.sourceRecordRow}`
                : ""}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {parsed ? (
              <button
                type="button"
                onClick={() => setShowRaw((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                {showRaw ? (
                  <>
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <rect x="1.5" y="3" width="13" height="10" rx="1" />
                      <path d="M1.5 6h13M5 6v7M11 6v7" />
                    </svg>
                    Felder
                  </>
                ) : (
                  <>
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M3 4.5h10M3 8h7M3 11.5h10" />
                    </svg>
                    Rohzeile
                  </>
                )}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Rohdaten schließen"
              className="rounded border border-slate-200 px-2 py-1 text-sm text-slate-500 hover:bg-slate-50"
            >
              ×
            </button>
          </div>
        </header>
        <div className="max-h-[70vh] overflow-auto p-4">
          {parsed && !showRaw ? (
            <table className="w-full text-sm">
              <tbody>
                {parsed.map((field, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}
                  >
                    <td className="whitespace-nowrap px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                      {field.label}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-slate-900">
                      {field.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <pre className="whitespace-pre-wrap break-words rounded-md bg-slate-950 p-3 font-mono text-xs leading-relaxed text-slate-100">
              {record.rawRecord ?? "Kein Rohdatensatz verfügbar."}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
